/**
 * #580 F-08 slice 4a3 (design I9, I11) — the payday type choice and the change warning, for
 * Settings AND onboarding (the `SampleWeekPanel` precedent: one component, the same true sentences).
 *
 * Choosing a type or day does not save. It opens the warning: every child, what they are paid and
 * how it splits into jars, and the completions still waiting for review. The saved value stays on
 * screen until the server commits. One confirmation sends ONE request, with the signed preview token
 * and a fresh operation key that is written down first and reused until its outcome is known.
 *
 * **Which state the server is in.** `probeChangeMode` asks for a no-change preview of the saved
 * value. `off` (409 PAYDAY_CHANGE_NOT_ENABLED) and `unknown` both mean: draw today's screen and send
 * today's PATCH. A PATCH that answers 409 PAYDAY_CONFIRMATION_REQUIRED calls `escalate`.
 *
 * **Unknown outcomes.** A lost answer, a server error or a receipt that does not match is never
 * reported as paid OR as rolled back. The operation and current settings are read first; `404` there is not proof that
 * nothing happened, so it only offers "Check original change", which resends the same key and body.
 * Only a refusal the server makes AFTER its replay check (stale, expired, invalid, draft repair,
 * not enabled) proves this key never committed, and only those release it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from './Button';
import { ApiError, api } from '../api/client.js';
import {
  NOT_ENABLED, clearPending, codeOf, fetchPreview, isUnchanged, newChangeKey, probeChangeMode, readChange,
  readPending, receiptMatches, rememberChangeMode, sendChange, writePending,
} from '../api/paydayChange.js';
import {
  AD_HOC, CALENDAR_MONTH, PAYDAY_PERIODS, WEEKDAY_NAMES_MONDAY_FIRST, dayNameFromPaydayPeriod,
  paydayKind, paydayPeriodFromDayName,
} from '../lib/payday.js';

/** The selector: every weekday (Monday first), monthly on the 1st (d-20260926-11), and ad hoc. */
export const PAYDAY_CHOICES = Object.freeze([
  ...WEEKDAY_NAMES_MONDAY_FIRST.map((day) => ({ value: paydayPeriodFromDayName(day), label: `Weekly · ${day}` })),
  { value: CALENDAR_MONTH, label: 'Monthly · first day of the month' },
  { value: AD_HOC, label: 'Ad hoc · whenever you choose' },
]);

/** The payday in a sentence. Never a weekday for monthly or ad hoc. */
export function paydayName(period) {
  const kind = paydayKind(period);
  if (kind === 'monthly') return 'monthly (the first day of the month)';
  if (kind === 'ad_hoc') return 'ad hoc (whenever you choose)';
  return dayNameFromPaydayPeriod(period);
}

const money = (c, currency) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(c / 100);
  } catch {
    return (c / 100).toFixed(2);
  }
};

const periodDate = (value, timezone) => new Intl.DateTimeFormat('en-US', { timeZone: timezone, month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));

export const COPY = Object.freeze({
  warning: (from, to, total) => `Changing payday from ${from} to ${to} will pay ${total} into your children's jars now, including money waiting for later or for a talk.`,
  warningNothing: (from, to) => `Changing payday from ${from} to ${to}. Nothing is ready to pay.`,
  pending: (n) => (n === 1 ? '1 completion still needs review. It is not included.' : `${n} completions still need review. They are not included.`),
  proposed: (to, from) => `Proposed: ${to}. Not saved yet — payday stays ${from} until you confirm.`,
  pay: (total) => `Pay ${total} and change payday`,
  change: 'Change payday',
  cancel: 'Cancel',
  success: (total, to) => `${total} was added to the children's jars. Payday is now ${to}.`,
  successNothing: (to) => `Nothing was ready to pay. Payday is now ${to}.`,
  stale: 'Payments changed since you looked. Review the new amounts before changing payday.',
  expired: 'This confirmation has expired. Look at the change again. Your payday setting was not changed.',
  invalid: 'This confirmation is not valid here. Look at the change again. Your payday setting was not changed.',
  already: (to) => `Payday is already ${to}. Nothing was paid and nothing was changed.`,
  rolledBack: 'This change did not go through. Your payday setting was not changed.',
  repair: (ids) => `A half-finished payment needs repair before payday can change, so nothing was paid and your payday setting was not changed. Ask for help with payout ${ids.map((id) => `#${id}`).join(', ')}.`,
  storage: 'This device could not keep the change’s record, so a refresh could not check it. Nothing was sent. Allow storage and try again.',
  lost: "We couldn't confirm the payment or payday change. Check its status before trying again.",
  busy: "We couldn't confirm the payment or payday change. Another payday change was in progress. Check again in a moment.",
  notParent: "We couldn't confirm the payment or payday change. Only a current parent of this family can change payday.",
  signedOut: "We couldn't confirm the payment or payday change. Please sign in again, then check it.",
  reauth: "We couldn't confirm the payment or payday change. Please confirm it is you, then check it.",
  previewFailed: "We couldn't load what this change would pay, so nothing was changed. Try again in a moment.",
  previewBusy: 'Another payday change was in progress, so nothing was changed. Try again in a moment.',
  previewNotParent: 'Only a current parent of this family can change payday. Nothing was changed.',
  unresolved: "An earlier payday change is not confirmed yet. Check it before making another.",
  check: 'Check original change',
  look: 'Look again',
});

/**
 * Refusals made after the server's replay check: this key never committed, so it is released.
 * NOT `OPERATION_KEY_REUSED`: the server says that only when this key already COMMITTED a different
 * change, so it is an unknown outcome and the operation is read first.
 */
const RELEASING = Object.freeze({
  PREVIEW_STALE: 'stale', PREVIEW_EXPIRED: 'expired', PREVIEW_INVALID: 'invalid',
  PAYOUT_DRAFT_REPAIR_REQUIRED: 'repair', [NOT_ENABLED]: 'rolledBack',
});
const LOOK_AGAIN = new Set(['stale', 'expired', 'invalid']);

/** What an UNRELEASED failure says. None of them claims that nothing happened. */
function unknownMessage(error) {
  if (error instanceof ApiError && error.status === 401) return COPY.signedOut;
  if (codeOf(error) === 'PARENT_REAUTH_REQUIRED') return COPY.reauth;
  if (error instanceof ApiError && error.status === 403) return COPY.notParent;
  if (codeOf(error) === 'PAYDAY_BUSY') return COPY.busy;
  return COPY.lost;
}

function previewMessage(error) {
  if (codeOf(error) === 'PAYDAY_BUSY') return COPY.previewBusy;
  if (error instanceof ApiError && error.status === 403) return COPY.previewNotParent;
  return COPY.previewFailed;
}

const IDLE = { phase: 'idle', preview: null, notice: null };

/**
 * @param {{tenantId?: string, actorId?: string|number, savedPeriod?: string,
 *          onCommitted?: (period: string) => void,
 *          queue?: (job: () => Promise<unknown>) => Promise<unknown>}} options
 *   `queue` runs each send after the host's other saves, so their answers settle in order.
 */
export function usePaydayChange({ tenantId, actorId, savedPeriod, onCommitted, queue }) {
  const [mode, setMode] = useState('unknown');
  const [state, setState] = useState(IDLE);
  const pendingRef = useRef(null);
  const sendingRef = useRef(false);
  const waiterRef = useRef(null);
  const committedRef = useRef(onCommitted);
  committedRef.current = onCommitted;
  const probedRef = useRef(null);
  const proposalRef = useRef(0);

  const settleWaiter = useCallback((ok, value) => {
    const waiter = waiterRef.current;
    waiterRef.current = null;
    if (!waiter) return;
    if (ok) waiter.resolve(value);
    else {
      const error = new Error(value);
      error.userMessage = value;
      waiter.reject(error);
    }
  }, []);

  const finish = useCallback((notice, ok) => {
    setState({ phase: 'idle', preview: null, notice });
    settleWaiter(ok, notice.message);
  }, [settleWaiter]);

  const committed = useCallback((answer, pending, currentPeriod = answer.newSetting.paydayPeriod) => {
    pendingRef.current = null;
    clearPending(tenantId, actorId);
    const to = currentPeriod ? paydayName(currentPeriod) : null;
    if (!to) {
      finish({ tone: 'ok', message: `${money(pending.totalCents, pending.currency)} was confirmed in the original change. The current payday setting could not be read.` }, true);
      return;
    }
    finish({ tone: 'ok', message: pending.totalCents > 0 ? COPY.success(money(pending.totalCents, pending.currency), to) : COPY.successNothing(to) }, true);
    if (typeof committedRef.current === 'function') committedRef.current(currentPeriod);
  }, [actorId, finish, tenantId]);

  /** A lost or untrusted answer: read the operation BEFORE saying anything. */
  const reconcile = useCallback(async (pending, fallback = COPY.lost) => {
    // Read settings after the operation settles: a parallel read could precede its commit.
    const [operation] = await Promise.allSettled([readChange(pending.operationKey)]);
    const [settings] = await Promise.allSettled([api.get('/api/tenants/current')]);
    const period = settings.status === 'fulfilled' ? settings.value?.tenant?.paydayPeriod : null;
    const current = PAYDAY_PERIODS.includes(period) ? period : null;
    if (operation.status === 'fulfilled' && receiptMatches(operation.value, pending)) {
      return committed(operation.value, pending, current);
    }
    // Settings prove only the setting. They never prove who was paid, or release the request.
    if (current && typeof committedRef.current === 'function') committedRef.current(current);
    const message = current === pending.to
      ? `Payday is now ${paydayName(current)}, but the payment receipt could not be read. Check the original change before trying again.`
      : fallback;
    return finish({ tone: 'warn', message, action: 'check' }, false);
  }, [committed, finish]);

  const send = useCallback(async (pending) => {
    let answer;
    try {
      answer = await sendChange(pending);
    } catch (error) {
      const released = RELEASING[codeOf(error)];
      if (released) {
        pendingRef.current = null;
        clearPending(tenantId, actorId);
        if (codeOf(error) === NOT_ENABLED) { setMode('off'); rememberChangeMode(tenantId, actorId, 'off'); }
        const ids = (error.body?.details?.repairRequired || []).map((r) => r.payoutId);
        const message = released === 'repair' ? COPY.repair(ids) : COPY[released];
        return finish({ tone: 'warn', message, ...(LOOK_AGAIN.has(released) ? { action: 'look', to: pending.to } : {}) }, false);
      }
      if (codeOf(error) === 'OPERATION_KEY_REUSED') return reconcile(pending);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return finish({ tone: 'warn', message: unknownMessage(error), action: 'check' }, false);
      }
      return reconcile(pending, unknownMessage(error));
    }
    if (receiptMatches(answer, pending)) return answer.replayed ? reconcile(pending) : committed(answer, pending);
    if (isUnchanged(answer, pending)) {
      // Definitive and write-free: the family already has this payday. Released, never "unresolved".
      pendingRef.current = null;
      clearPending(tenantId, actorId);
      finish({ tone: 'info', message: COPY.already(paydayName(pending.to)) }, true);
      if (typeof committedRef.current === 'function') committedRef.current(pending.to);
      return undefined;
    }
    return reconcile(pending);
  }, [actorId, committed, finish, reconcile, tenantId]);

  /** One send at a time, and through the host's queue when it has one. */
  const run = useCallback((pending) => {
    if (sendingRef.current) return undefined;
    sendingRef.current = true;
    setState((prev) => ({ ...prev, phase: 'sending' }));
    const job = () => send(pending);
    return Promise.resolve(typeof queue === 'function' ? queue(job) : job())
      .finally(() => { sendingRef.current = false; });
  }, [queue, send]);

  // The mode, once per family and parent. And an unresolved change from before a refresh is read.
  useEffect(() => {
    const scope = `${tenantId}|${actorId}`;
    if (!tenantId || !PAYDAY_PERIODS.includes(savedPeriod) || probedRef.current === scope) return undefined;
    probedRef.current = scope;
    // Kept while this family and parent are shown, even if the saved payday changes meanwhile.
    probeChangeMode(savedPeriod, tenantId, actorId).then((next) => { if (probedRef.current === scope) setMode(next); });
    const stored = readPending(tenantId, actorId);
    if (stored) {
      pendingRef.current = stored;
      // The panel (and its live region) is on screen BEFORE the answer, so the answer is spoken.
      setState({ phase: 'checking', preview: null, notice: null });
      reconcile(stored);
    }
    return undefined;
  }, [actorId, reconcile, savedPeriod, tenantId]);

  /** @returns {Promise<'legacy'|'shown'|'same'|'refused'>} `legacy`: send today's PATCH instead. */
  const propose = useCallback(async (target) => {
    if (sendingRef.current) return 'refused';
    const proposal = ++proposalRef.current;
    if (pendingRef.current) {
      setState({ phase: 'idle', preview: null, notice: { tone: 'warn', message: COPY.unresolved, action: 'check' } });
      return 'refused';
    }
    setState({ phase: 'loading', preview: null, notice: null });
    try {
      const preview = await fetchPreview(target);
      if (proposal !== proposalRef.current) return 'refused';
      if (preview.noChange) {
        setState(IDLE);
        return 'same';
      }
      setMode('on');
      rememberChangeMode(tenantId, actorId, 'on');
      if (preview.repairRequired.length) {
        finish({ tone: 'warn', message: COPY.repair(preview.repairRequired.map((r) => r.payoutId)) }, false);
        return 'refused';
      }
      setState({ phase: 'warning', preview, notice: null });
      return 'shown';
    } catch (error) {
      if (proposal !== proposalRef.current) return 'refused';
      if (codeOf(error) === NOT_ENABLED) {
        setMode('off');
        rememberChangeMode(tenantId, actorId, 'off');
        setState(IDLE);
        return 'legacy';
      }
      finish({ tone: 'warn', message: previewMessage(error) }, false);
      return 'refused';
    }
  }, [actorId, finish, tenantId]);

  const confirm = useCallback(() => {
    const preview = state.preview;
    if (!preview || sendingRef.current || pendingRef.current) return undefined;
    const pending = {
      operationKey: newChangeKey(), from: preview.from, to: preview.to, previewToken: preview.previewToken,
      currency: preview.currency, totalCents: preview.totalCents,
      recipients: preview.recipients.map((r) => ({ userId: r.userId, totalCents: r.totalCents, jarCents: r.jarCents, batches: r.batches })),
    };
    // No money request without a durable copy to recover after a refresh.
    if (!writePending(tenantId, actorId, pending)) {
      finish({ tone: 'warn', message: COPY.storage, action: 'look', to: preview.to }, false);
      return undefined;
    }
    pendingRef.current = pending;
    proposalRef.current += 1;
    return run(pending);
  }, [actorId, finish, run, state.preview, tenantId]);

  /** "Check original change": the SAME key and body again. The server replays a committed change. */
  const recheck = useCallback(() => (pendingRef.current ? run(pendingRef.current) : undefined), [run]);

  const cancel = useCallback(() => {
    proposalRef.current += 1;
    finish({ tone: 'info', message: 'Payday was not changed.' }, false);
  }, [finish]);

  /** Onboarding waits on the parent's answer to the warning it opened. */
  const awaitDecision = useCallback(() => new Promise((resolve, reject) => {
    waiterRef.current = { resolve, reject };
  }), []);

  return { mode, state, unresolved: Boolean(pendingRef.current), propose, confirm, recheck, cancel, awaitDecision, escalate: propose };
}

/** The warning, the outcome sentence and the one recovery button, from `usePaydayChange`. */
export default function PaydayChangePanel({ change, className }) {
  const { state, confirm, cancel, recheck, propose } = change;
  const { phase, preview, notice } = state;
  const sending = phase === 'sending';
  const sectionRef = useRef(null);
  const confirmRef = useRef(null);
  const recoveryRef = useRef(null);
  const noticeRef = useRef(null);

  // The warning is often off screen (onboarding draws it above the review): bring it into view,
  // and put the parent's finger on its confirm button.
  useEffect(() => {
    if (!preview) return;
    const section = sectionRef.current;
    if (section && typeof section.scrollIntoView === 'function') section.scrollIntoView({ block: 'nearest' });
    if (confirmRef.current) confirmRef.current.focus();
  }, [preview]);

  // An outcome unmounts the button that was pressed. Focus goes to the recovery button when there
  // is one, otherwise to the notice, but only when focus was lost or was already in this panel.
  useEffect(() => {
    if (!notice) return;
    const active = typeof document === 'undefined' ? null : document.activeElement;
    const lost = !active || active === document.body || (sectionRef.current && sectionRef.current.contains(active));
    if (!lost) return;
    const target = recoveryRef.current || noticeRef.current;
    if (target) target.focus();
  }, [notice]);

  if (phase === 'idle' && !notice) return null;
  const total = preview ? money(preview.totalCents, preview.currency) : '';
  // The warning sentence IS the live region's text while it is shown, so it is announced.
  const spoken = notice?.message || (preview
    ? (preview.totalCents > 0
      ? COPY.warning(paydayName(preview.from), paydayName(preview.to), total)
      : COPY.warningNothing(paydayName(preview.from), paydayName(preview.to)))
    : '');

  return (
    <section
      ref={sectionRef}
      className={['pcp', className].filter(Boolean).join(' ')}
      data-component="PaydayChangePanel"
      aria-label="Changing payday"
      aria-busy={sending || phase === 'loading' || phase === 'checking' ? 'true' : undefined}
      style={{ display: 'grid', gap: 'var(--s-2, 8px)' }}
    >
      <p ref={noticeRef} tabIndex={-1} role="status" aria-live="polite" data-tone={notice?.tone || 'info'} style={{ margin: 0 }}>{spoken}</p>
      {preview ? (
        <>
          <p style={{ margin: 0 }}>{COPY.proposed(paydayName(preview.to), paydayName(preview.from))}</p>
          {preview.recipients.length > 0 ? (
            <ul style={{ margin: 0 }}>
              {preview.recipients.map((r) => (
                <li key={r.userId}>
                  {`${r.name}: ${money(r.totalCents, preview.currency)} — Save ${money(r.jarCents[0], preview.currency)} · Give ${money(r.jarCents[1], preview.currency)} · Spend ${money(r.jarCents[2], preview.currency)}`}
                  <ul>
                    {r.batches.map((batch, index) => (
                      <li key={index}>{`${periodDate(batch.periodStart, preview.timezone)} to ${periodDate(batch.periodEnd, preview.timezone)} (end exclusive): ${money(batch.totalCents, preview.currency)}`}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : null}
          {preview.pendingReviewCount > 0 ? <p style={{ margin: 0 }}>{COPY.pending(preview.pendingReviewCount)}</p> : null}
          <div role="group" aria-label="Confirm the payday change" style={{ display: 'flex', gap: 'var(--s-2, 8px)', flexWrap: 'wrap' }}>
            <Button innerRef={confirmRef} variant="primary" size="sm" onClick={confirm} disabled={sending} loading={sending} busyLabel="Changing payday…">
              {preview.totalCents > 0 ? COPY.pay(total) : COPY.change}
            </Button>
            <Button variant="quiet" size="sm" onClick={cancel} disabled={sending}>{COPY.cancel}</Button>
          </div>
        </>
      ) : null}
      {notice?.action === 'check' ? (
        <Button innerRef={recoveryRef} variant="quiet" size="sm" onClick={recheck} disabled={sending} loading={sending} busyLabel="Checking…">{COPY.check}</Button>
      ) : null}
      {notice?.action === 'look' ? (
        <Button innerRef={recoveryRef} variant="quiet" size="sm" onClick={() => propose(notice.to)}>{COPY.look}</Button>
      ) : null}
    </section>
  );
}
